import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PlacowkaService from '@/services/PlacowkaService'

const emptyDetails = {
	nazwa: '',
	adres: '',
	miasto: '',
	wojewodztwo: '',
	typ: '',
}

const normalizeDetails = data => ({
	nazwa: data?.nazwa || '',
	adres: data?.adres || '',
	miasto: data?.id_miasto?.nazwa || data?.miasto || '',
	wojewodztwo: data?.id_wojewodztwo?.nazwa || data?.wojewodztwo || '',
	typ: data?.id_typ?.typ || data?.typ || '',
})

export default function KorektaPlacowkaSection({
	placowkaId,
	placowkaName,
	infaktClientCompanyName,
	onAssignClick,
}) {
	const { t } = useTranslation()
	const [details, setDetails] = useState(emptyDetails)
	const [loading, setLoading] = useState(false)
	const [loadError, setLoadError] = useState(false)

	const loadPlacowka = useCallback(async id => {
		if (!id) return
		setLoading(true)
		setLoadError(false)
		const { data, error } = await PlacowkaService.getById(id)
		if (error || !data) {
			setLoadError(true)
			setDetails(emptyDetails)
		} else {
			setDetails(normalizeDetails(data))
		}
		setLoading(false)
	}, [])

	useEffect(() => {
		if (!placowkaId) {
			setDetails(emptyDetails)
			setLoadError(false)
			return
		}
		loadPlacowka(placowkaId)
	}, [loadPlacowka, placowkaId])

	const displayValue = value => {
		if (loading) return t('placowkaCard.loading')
		if (loadError) return t('placowkaCard.errorLoading')
		return value || t('placowkaCard.na')
	}

	const infaktName = (infaktClientCompanyName || '').trim() || t('korekty.list.table.na')

	const fields = [
		{ key: 'infakt', label: t('korekty.list.table.infaktClientCompanyName'), value: infaktName },
		{ key: 'name', label: t('placowkaCard.labels.name'), value: displayValue(details.nazwa || placowkaName), link: true },
		{ key: 'address', label: t('placowkaCard.labels.address'), value: displayValue(details.adres) },
		{ key: 'city', label: t('placowkaCard.labels.city'), value: displayValue(details.miasto) },
		{ key: 'voivodeship', label: t('placowkaCard.labels.voivodeship'), value: displayValue(details.wojewodztwo) },
		{ key: 'type', label: t('placowkaCard.labels.type'), value: displayValue(details.typ) },
	]

	return (
		<section className="mt-6">
			<div className="rounded-xl border border-gray-200 bg-white shadow-sm">
				<header className="flex items-center justify-between gap-3 border-b border-gray-200 p-6">
					<h2 className="text-lg font-semibold text-gray-900">
						{t('korekty.details.placowka.title')}
					</h2>
					{placowkaId ? (
						<button
							type="button"
							onClick={onAssignClick}
							className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-900 hover:bg-gray-50">
							{t('korekty.list.table.changePlacowka')}
						</button>
					) : null}
				</header>

				<div className="p-6">
					{placowkaId ? (
						<div className="grid gap-4 text-sm [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
							{fields.map(({ key, label, value, link }) => (
								<div key={key} className="min-w-0">
									<div className="text-gray-600">{label}</div>
									<div className="font-medium text-gray-900 break-words whitespace-pre-wrap">
										{link && placowkaId && !loading && !loadError ? (
											<Link
												to={`/klienci/placowki/details/${placowkaId}`}
												className="text-blue-600 hover:text-blue-800 hover:underline">
												{value}
											</Link>
										) : (
											value
										)}
									</div>
								</div>
							))}
						</div>
					) : (
						<div>
							<div className="mb-4">
								<div className="text-sm text-gray-600">
									{t('korekty.list.table.infaktClientCompanyName')}
								</div>
								<div className="font-medium text-gray-900 break-words">{infaktName}</div>
							</div>
							<p className="text-sm text-gray-500">
								{t('korekty.details.placowka.emptyDescription')}
							</p>
							<button
								type="button"
								onClick={onAssignClick}
								className="mt-4 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700">
								{t('korekty.list.table.assignPlacowka')}
							</button>
						</div>
					)}
				</div>
			</div>
		</section>
	)
}
